import React from 'react';
import { Box, Typography } from '@mui/material';
import { headingLetterSpacing } from '../../../utils/headingTypography';

const INLINE_BOLD_REGEX = /(\*\*[^*]+\*\*)/g;
const INLINE_LINK_REGEX = /<a\b([^>]*)>(.*?)<\/a>/gis;
const STANDALONE_BOLD_REGEX = /^\*\*([^*]+)\*\*$/;
const BULLET_REGEX = /^[•✓-]\s+(.*)$/;
const HEADING_TOP_MARGIN = '20px';
const HEADING_TO_PARAGRAPH_GAP = '4px';
const HEADING_TO_LIST_GAP = '8px';
const DEFAULT_BLOCK_GAP = '16px';

const renderInlineBold = (text, keyPrefix = 'inline') =>
  text.split(INLINE_BOLD_REGEX).map((part, index) =>
    /^\*\*[^*]+\*\*$/.test(part) ? (
      <strong key={`${keyPrefix}-bold-${index}`} style={{ fontWeight: 700 }}>
        {part.slice(2, -2)}
      </strong>
    ) : (
      part
    )
  );

const parseLinkAttributes = (attributeString = '') => {
  const attributes = {};
  const attributeRegex = /(\w[\w-]*)\s*=\s*(['"])(.*?)\2/g;

  attributeString.replace(attributeRegex, (_, name, __, value) => {
    attributes[name.toLowerCase()] = value;
    return '';
  });

  return attributes;
};

const renderInlineText = (text, keyPrefix) => {
  if (!text) {
    return '';
  }

  INLINE_LINK_REGEX.lastIndex = 0;

  const nodes = [];
  let lastIndex = 0;
  let linkIndex = 0;
  let match;

  while ((match = INLINE_LINK_REGEX.exec(text)) !== null) {
    const before = text.slice(lastIndex, match.index);

    if (before) {
      nodes.push(...renderInlineBold(before, `${keyPrefix}-text-${linkIndex}`));
    }

    const attrs = parseLinkAttributes(match[1]);
    const href = attrs.href || undefined;
    const target = attrs.target || undefined;
    const rel =
      attrs.rel || (target === '_blank' ? 'noreferrer noopener' : undefined);
    const innerText = match[2] || '';

    nodes.push(
      <Box
        key={`${keyPrefix}-link-${linkIndex}`}
        component="a"
        href={href}
        target={target}
        rel={rel}
        sx={{
          color: '#C7CB00',
          textDecoration: 'underline',
          fontWeight: 700,
          display: 'inline',
        }}
      >
        {renderInlineBold(innerText, `${keyPrefix}-link-${linkIndex}`)}
      </Box>
    );

    lastIndex = match.index + match[0].length;
    linkIndex += 1;
  }

  const after = text.slice(lastIndex);

  if (after) {
    nodes.push(...renderInlineBold(after, `${keyPrefix}-tail`));
  }

  return nodes.length ? nodes : text;
};

const getBlocks = (text) =>
  text
    .split(/\n\n+/)
    .map((block) => block.trim())
    .filter(Boolean);

const getBulletItems = (block) => {
  const lines = block
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  if (!lines.length || !lines.every((line) => BULLET_REGEX.test(line))) {
    return null;
  }

  return lines.map((line) => line.replace(BULLET_REGEX, '$1').trim());
};

const renderKeyPointText = (
  text,
  { paragraphSx = {}, headingSx = {}, listSx = {}, listItemSx = {} } = {}
) => {
  let previousType = null;

  return getBlocks(text).map((block, index) => {
    const headingMatch = block.match(STANDALONE_BOLD_REGEX);
    const bulletItems = getBulletItems(block);
    let node;

    if (headingMatch) {
      const resolvedHeadingSx = {
        ...headingSx,
        letterSpacing:
          headingLetterSpacing(headingSx.fontSize) ?? headingSx.letterSpacing,
      };

      node = (
        <Typography
          key={index}
          component="h4"
          sx={{
            mt: previousType ? HEADING_TOP_MARGIN : 0,
            mb: 0,
            ...resolvedHeadingSx,
          }}
        >
          {headingMatch[1]}
        </Typography>
      );
      previousType = 'heading';
      return node;
    }

    if (bulletItems) {
      node = (
        <Box
          key={index}
          component="ul"
          sx={{
            mt:
              previousType === 'heading'
                ? HEADING_TO_LIST_GAP
                : previousType
                  ? DEFAULT_BLOCK_GAP
                  : 0,
            mb: 0,
            pl: '1.25rem',
            ...listSx,
          }}
        >
          {bulletItems.map((item, itemIndex) => (
            <Box
              key={`${index}-${itemIndex}`}
              component="li"
              sx={{
                mb: itemIndex === bulletItems.length - 1 ? 0 : '8px',
              }}
            >
              <Typography component="span" sx={listItemSx}>
                {renderInlineText(item, `${index}-${itemIndex}`)}
              </Typography>
            </Box>
          ))}
        </Box>
      );
      previousType = 'list';
      return node;
    }

    node = (
      <Typography
        key={index}
        sx={{
          mt:
            previousType === 'heading'
              ? HEADING_TO_PARAGRAPH_GAP
              : previousType
                ? DEFAULT_BLOCK_GAP
                : 0,
          mb: 0,
          ...paragraphSx,
        }}
      >
        {renderInlineText(block, `${index}`)}
      </Typography>
    );
    previousType = 'paragraph';
    return node;
  });
};

export default renderKeyPointText;
